import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { ClickHandlerService } from '@services/core/click-handler.service';
import { SharedModule } from '@shared/modules/shared.module';
import { Subscription } from 'rxjs';

// Definición de la estructura de los items del menú
interface MenuItem {
	label: string;
	id: string;
	icon?: string;
	routerLink?: string[];
	items?: MenuItem[]; // Submenús
	action?: () => void; // Nueva propiedad para aceptar acciones desde el padre
}

@Component({
	selector: 'app-item-menu',
	imports: [SharedModule],
	templateUrl: './item-menu.component.html',
	styleUrl: './item-menu.component.scss',
})
export class ItemMenuComponent implements OnInit, OnDestroy {
	@Input() item!: MenuItem;
	@Input() index = 0;
	@Input() root = false;
	@Input() isSidebarOpen = true;
	@Output() itemClick = new EventEmitter<MenuItem>();
	@Input() forceExpanded = false;
	@ViewChild('submenuTemplate') submenuTemplate!: any;

	expanded = false;
	private overlayRef: OverlayRef | null = null;
	private clickSubscription = new Subscription();

	constructor(
		private overlay: Overlay,
		private elRef: ElementRef,
		private viewContainerRef: ViewContainerRef,
		private clickHandlerService: ClickHandlerService,
	) {}

	toggleSubmenu(event?: Event) {
		if (this.item.items && this.item.items.length > 0) {
			this.expanded = !this.expanded;
			// Detener la propagación del evento para evitar que se ejecute handleClick
			if (event) {
				event.stopPropagation();
			}
		}
	}

	ngOnInit(): void {
		this.clickSubscription = this.clickHandlerService.click$.subscribe((event: Event) => {
			if (!this.elRef.nativeElement.contains(event.target)) {
				this.expanded = false;
				this.closeOverlay();
			}
		});
	}

	ngOnDestroy(): void {
		this.clickSubscription.unsubscribe();
		this.closeOverlay();
	}

	handleClick(): void {
		if (this.item.action) {
			this.item.action();
			this.itemClick.emit(this.item);
		} else if (this.item.items && this.item.items.length > 0) {
			if (this.isSidebarOpen) {
				this.expanded = !this.expanded;
			} else {
				this.toggleOverlayMenu();
			}
		} else {
			this.itemClick.emit(this.item);
		}
	}

	toggleOverlayMenu(): void {
		if (this.overlayRef) {
			this.closeOverlay();
			return;
		}

		const positionStrategy = this.overlay
			.position()
			.flexibleConnectedTo(this.elRef.nativeElement)
			.withPositions([
				{
					originX: 'end',
					originY: 'top',
					overlayX: 'start',
					overlayY: 'top',
					offsetX: 8,
				},
			]);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			scrollStrategy: this.overlay.scrollStrategies.close(),
			hasBackdrop: true,
			backdropClass: 'cdk-overlay-transparent-backdrop',
		});

		const portal = new TemplatePortal(this.submenuTemplate, this.viewContainerRef);
		this.overlayRef.attach(portal);
		this.overlayRef.backdropClick().subscribe(() => this.closeOverlay());
	}

	closeOverlay(): void {
		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = null;
		}
	}
}
